import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Header from "../Components/Header";
import Spinner from "react-bootstrap/Spinner";
import axios from "axios";
import { faAward, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import StudentSdebar from "./StudentSdebar";
import { motion } from "framer-motion";
import VLogo from '../Images/V_Logo.png'; 
// --- IMPORT THE CERTIFICATE BACKGROUND IMAGE ---
import CertificateBG from '../Images/certificate.png'; 

// --- Helper Component for Skeleton Loading UI ---
const SkeletonCard = () => (
  <div className="col-md-4 col-sm-6 col-12 mb-4">
    <div style={styles.card}>
      <div style={styles.skeletonImage}></div>
      <div style={{ padding: "1rem" }}>
        <div style={styles.skeletonTitle}></div>
        <div style={styles.skeletonText}></div>
        <div style={styles.skeletonButton}></div>
      </div>
    </div>
  </div>
);

const Certificates = () => {
  const [displayedCertificates, setDisplayedCertificates] = useState(6);
  const [certificateData, setCertificateData] = useState([]);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [viewLoadingId, setViewLoadingId] = useState(null);
  const token = localStorage.getItem("jwttoken");

  useEffect(() => {
    const fetchCertificates = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `https://kaasbackend.vetrinahealthcare.com/master/list-certificates-v2/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCertificateData(response.data.certificates || []);
      } catch (err) {
        console.error("Error fetching certificates:", err);
        setCertificateData([]);
      } finally {
        setTimeout(() => setIsLoading(false), 500);
      }
    };

    if (token) {
      fetchCertificates();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const handleViewAll = () => setDisplayedCertificates(certificateData.length);
  const handleViewLess = () => setDisplayedCertificates(6);

  const handleViewCertificate = async (cert) => {
    if (!cert || !cert.view_url) {
      console.error("Certificate URL not found.");
      alert("Could not view certificate. URL is missing.");
      return;
    }

    setViewLoadingId(cert.course_id);
    const newTab = window.open("", "_blank");

    if (newTab) {
      // --- Professional Loader HTML for the new window ---
      const loaderHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Loading Certificate...</title>
            <link rel="icon" type="image/png" href="${VLogo}">
            <style>
              body {
                margin: 0;
                height: 100vh;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #2c3e50;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              }
              .loader-container {
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .loader {
                width: 60px;
                height: 60px;
                border: 6px solid rgba(255, 255, 255, 0.3);
                border-top-color: #B8529E;
                border-radius: 50%;
                animation: spin 1s linear infinite;
              }
              .loader-text {
                color: #fff;
                margin-top: 20px;
                font-size: 1.1rem;
              }
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            </style>
          </head>
          <body>
            <div class="loader-container">
              <div class="loader"></div>
              <p class="loader-text">Preparing Certificate...</p>
            </div>
          </body>
        </html>
      `;
      newTab.document.write(loaderHtml);
    } else {
      alert('Popup blocked! Please allow popups for this site.');
      setViewLoadingId(null);
      return;
    }

    try {
      const response = await axios.get(cert.view_url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const finalHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <link rel="icon" type="image/png" href="${VLogo}">
            <title>VetLearn Certificate</title>
            <style>
              body { margin: 0; height: 100vh; overflow: hidden; background-color: #525659; }
              iframe { border: none; width: 100%; height: 100%; }
            </style>
          </head>
          <body>
            <iframe src="${pdfUrl}" type="application/pdf"></iframe>
          </body>
        </html>
      `;
      
      newTab.document.open();
      newTab.document.write(finalHtml);
      newTab.document.close();

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);

    } catch (error) {
      console.error("Failed to fetch certificate:", error);
      if (newTab) newTab.close();
      alert("Failed to load certificate. Please try again.");
    } finally {
      setViewLoadingId(null);
    }
  };

  const scrollStyles = {
    scrollbarWidth: "thin",
    scrollbarColor: "#B8529E #f0f0f5",
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f4f4f9", overflow: "hidden" }}>
      <Header />
      <div style={{ display: "flex", marginTop: "60px", height: "calc(100vh - 60px)" }}>
        <StudentSdebar onToggle={setSidebarExpanded} />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          style={{ ...styles.mainContent, marginLeft: sidebarExpanded ? "240px" : "60px", ...scrollStyles }}
        >
          <div style={styles.contentWrapper}>
            {/* --- Centered Header --- */}
            <div style={styles.headerContainer}>
              <h1 style={styles.headerTitle}><FontAwesomeIcon icon={faAward} /> My Certificates</h1>
            </div>

            <div style={styles.controlsContainer}>
              {certificateData.length > 6 && (
                displayedCertificates <= 6 ? (
                  <button onClick={handleViewAll} style={styles.viewButton}>
                    View All ({certificateData.length})
                  </button>
                ) : (
                  <button onClick={handleViewLess} style={styles.viewButton}>
                    View Less
                  </button>
                )
              )}
            </div>

            <div className="row" style={{ marginTop: "20px" }}>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => <SkeletonCard key={index} />)
              ) : certificateData.length > 0 ? (
                certificateData.slice(0, displayedCertificates).map((cert) => (
                  <div key={cert.certificate_code} className="col-md-4 col-sm-6 col-12 mb-4 d-flex align-items-stretch">
                    <div style={styles.card}>
                      {/* --- UPDATED CARD HEADER WITH HEAVIER BLUR --- */}
                      <div style={styles.cardImageContainer}>
                        <div style={styles.cardImageBackground}></div>
                        <div style={styles.cardImageOverlay}></div>
                        <div style={styles.cardRibbon}>VETLEARN</div>
                      </div>
                      <div style={styles.cardBody}>
                        <h5 style={styles.cardTitle}>{cert.course_title}</h5>
                        <p style={styles.cardText}>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#28a745', marginRight: '8px' }} />
                          Issued on: {cert.issued_on}
                        </p>
                        <button
                          onClick={() => handleViewCertificate(cert)}
                          disabled={viewLoadingId === cert.course_id}
                          style={styles.cardButton}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7D52A0'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#B8529E'}
                        >
                          {viewLoadingId === cert.course_id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            "View Certificate"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12 text-center mt-5">
                  <p style={styles.noCertificatesText}>
                    You haven't earned any certificates yet.
                  </p>
                  <p className="text-muted">Complete a course to see your achievements here!</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// --- UPDATED Professional Styles Object ---
const styles = {
  mainContent: { flexGrow: 1, overflowY: "auto", padding: "30px", transition: "margin-left 0.3s ease-in-out" },
  contentWrapper: { backgroundColor: "white", borderRadius: "12px", padding: "30px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)", minHeight: "85vh" },
  headerContainer: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  headerTitle: {
    display: 'inline-block',
    padding: '12px 30px',
    background: 'linear-gradient(to right, #3953A3, #7D52A0, #B8529E)',
    color: 'white',
    borderRadius: '50px',
    fontSize: "24px",
    fontWeight: '600',
    boxShadow: '0 4px 15px rgba(125, 82, 160, 0.4)',
  },
  controlsContainer: { textAlign: "end", marginBottom: "10px" },
  viewButton: { backgroundColor: "transparent", border: "1px solid #B8529E", color: "#B8529E", borderRadius: "20px", padding: "8px 20px", cursor: "pointer", fontWeight: "600", transition: "all 0.3s ease" },
  noCertificatesText: { fontSize: "1.2rem", color: "#555", fontWeight: "500" },
  card: { backgroundColor: '#fff', borderRadius: '15px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)', transition: 'transform 0.3s ease, box-shadow 0.3s ease', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '1px solid #eee' },
  
  // --- STYLES FOR BLURRED IMAGE BACKGROUND ---
  cardImageContainer: { 
    height: '160px', 
    position: 'relative', 
    overflow: 'hidden' 
  },
  cardImageBackground: {
    backgroundImage: `url(${CertificateBG})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    // --- INCREASED BLUR VALUE ---
    filter: 'blur(10px)',
    transform: 'scale(1.2)', // Slightly increased scale for better edge coverage
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  cardImageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // --- Slightly darkened overlay ---
    backgroundColor: 'rgba(57, 83, 163, 0.4)',
    zIndex: 2,
  },
  cardRibbon: { 
    position: 'absolute', 
    top: '10px', 
    right: '-30px', 
    backgroundColor: '#B8529E', 
    color: 'white', 
    padding: '5px 40px', 
    fontSize: '12px', 
    fontWeight: 'bold', 
    transform: 'rotate(45deg)', 
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    zIndex: 3,
  },

  cardBody: { padding: '20px', display: 'flex', flexDirection: 'column', flexGrow: 1 },
  cardTitle: { color: '#333', fontSize: '1.2rem', fontWeight: '600', minHeight: '50px', marginBottom: '10px' },
  cardText: { color: '#666', fontSize: '0.9rem', flexGrow: 1, marginBottom: '20px' },
  cardButton: { backgroundColor: '#B8529E', border: 'none', color: 'white', borderRadius: '8px', height: '40px', width: '100%', cursor: 'pointer', transition: 'background-color 0.3s ease', fontWeight: '600', fontSize: '1rem' },
  skeletonImage: { height: '160px', backgroundColor: '#e0e0e0' },
  skeletonTitle: { height: '24px', width: '80%', backgroundColor: '#e0e0e0', borderRadius: '4px', marginBottom: '10px' },
  skeletonText: { height: '16px', width: '60%', backgroundColor: '#e0e0e0', borderRadius: '4px', marginBottom: '20px' },
  skeletonButton: { height: '40px', width: '100%', backgroundColor: '#e0e0e0', borderRadius: '8px' },
};

export default Certificates;